import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Smartphone, Tag } from 'lucide-react';
import { DispositivoInterface } from '../../interfaces/DispositivoInterface';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../global/global';

interface TagDisponible {
  sentinel_id: string;
  friendly_name: string;
  beacon_type: string;
  sentinel_type: string;
  is_assigned: boolean;
}

const EditarDispositivoSimple: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tagsDisponibles, setTagsDisponibles] = useState<TagDisponible[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagsInfo, setTagsInfo] = useState<{total: number, assigned: number, available: number}>({
    total: 0,
    assigned: 0,
    available: 0
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<DispositivoInterface>();

  // Cargar datos simulados para prueba
  useEffect(() => {
    const cargarDatos = async () => {
      if (!id) {
        navigate('/dispositivos');
        return;
      }

      try {
        setLoading(true);
        setLoadingTags(true);

        // Simular datos del dispositivo para prueba
        const dispositivoSimulado = {
          id: parseInt(id),
          nombre_dispositivo: 'Samsung Galaxy S21',
          imei: '123456789012345',
          numero_serie: 'SN123456789',
          marca: 'Samsung',
          modelo: 'Galaxy S21',
          color: 'Negro',
          estatus_id: 1,
          capacidad_almacenamiento: '128GB',
          ram: '8GB',
          version_android: '13.0',
          nivel_api: 33,
          ubicacion_actual: 'Oficina Principal',
          responsable_actual: 'Juan Pérez',
          proveedor: 'Samsung Electronics',
          fecha_adquisicion: '2024-01-15',
          observaciones: 'Dispositivo en buen estado',
          sentinel_id: '' // Sin tag asignado inicialmente
        };

        // Llenar el formulario
        Object.keys(dispositivoSimulado).forEach((key) => {
          const value = dispositivoSimulado[key as keyof typeof dispositivoSimulado];
          if (value !== null && value !== undefined) {
            setValue(key as keyof DispositivoInterface, value);
          }
        });

        // Cargar tags disponibles desde el endpoint real
        const tagsResponse = await fetch(`${API}/dispositivos/tags-disponibles`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (tagsResponse.ok) {
          const tagsData = await tagsResponse.json();
          setTagsDisponibles(tagsData.data || []);
          
          // Actualizar información de tags
          setTagsInfo({
            total: tagsData.count || 0,
            assigned: tagsData.assigned_count || 0,
            available: tagsData.available_count || 0
          });
          
          // Log para debug
          console.log('Tags cargados:', {
            total: tagsData.count,
            asignados: tagsData.assigned_count,
            disponibles: tagsData.available_count
          });
        } else {
          // Fallback si el endpoint autenticado falla
          const fallbackResponse = await fetch(`${API}/tags-test`);
          const fallbackData = await fallbackResponse.json();
          setTagsDisponibles(fallbackData.data || []);
        }
        
      } catch (error) {
        console.error('Error cargando datos:', error);
        toast.error('Error al cargar los datos del dispositivo');
      } finally {
        setLoading(false);
        setLoadingTags(false);
      }
    };

    cargarDatos();
  }, [id, setValue, navigate]);

  const onSubmit = async (data: DispositivoInterface) => {
    if (!id || !token) return;

    try {
      setSaving(true);
      
      // Simular guardado exitoso
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Datos a guardar:', data);
      toast.success('Dispositivo actualizado exitosamente');
      navigate('/dispositivos');
    } catch (error) {
      console.error('Error actualizando dispositivo:', error);
      toast.error('Error al actualizar el dispositivo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos del dispositivo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dispositivos')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver a la lista
          </button>
          <div className="flex items-center">
            <Smartphone className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Editar Dispositivo</h1>
              <p className="text-gray-600">Modifica la información del dispositivo Samsung</p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            
            {/* Información Básica */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Información Básica</h3>
            </div>
            <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Dispositivo *
                </label>
                <input
                  type="text"
                  {...register('nombre_dispositivo', { required: 'El nombre del dispositivo es requerido' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Samsung Galaxy S21"
                />
                {errors.nombre_dispositivo && (
                  <p className="mt-1 text-sm text-red-600">{errors.nombre_dispositivo.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IMEI *
                </label>
                <input
                  type="text"
                  {...register('imei', { required: 'El IMEI es requerido' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: 123456789012345"
                />
                {errors.imei && (
                  <p className="mt-1 text-sm text-red-600">{errors.imei.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Serie *
                </label>
                <input
                  type="text"
                  {...register('numero_serie', { required: 'El número de serie es requerido' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: SN123456789"
                />
                {errors.numero_serie && (
                  <p className="mt-1 text-sm text-red-600">{errors.numero_serie.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marca *
                </label>
                <input
                  type="text"
                  {...register('marca', { required: 'La marca es requerida' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Samsung"
                />
                {errors.marca && (
                  <p className="mt-1 text-sm text-red-600">{errors.marca.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo *
                </label>
                <input
                  type="text"
                  {...register('modelo', { required: 'El modelo es requerido' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Galaxy S21"
                />
                {errors.modelo && (
                  <p className="mt-1 text-sm text-red-600">{errors.modelo.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <input
                  type="text"
                  {...register('color')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Negro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estatus
                </label>
                <select
                  {...register('estatus_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>Disponible</option>
                  <option value={2}>Asignado</option>
                  <option value={3}>Mantenimiento</option>
                  <option value={4}>Dañado</option>
                </select>
              </div>
            </div>

            {/* Asignación de Tag OnAsset */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Tag className="h-5 w-5 mr-2" />
                Asignación de Tag
                {tagsInfo.total > 0 && (
                  <div className="ml-3 flex gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {tagsInfo.total} total
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {tagsInfo.available} disponibles
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                      {tagsInfo.assigned} asignados
                    </span>
                  </div>
                )}
              </h3>
            </div>
            <div className="px-6 py-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tag
                </label>
                {loadingTags ? (
                  <div className="flex items-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Cargando tags disponibles...</span>
                  </div>
                ) : (
                  <select
                    {...register('sentinel_id')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin asignar</option>
                    {tagsDisponibles.map((tag) => (
                      <option 
                        key={tag.sentinel_id} 
                        value={tag.sentinel_id}
                        disabled={tag.is_assigned && watch('sentinel_id') !== tag.sentinel_id}
                        className={tag.is_assigned && watch('sentinel_id') !== tag.sentinel_id ? 'text-gray-400' : ''}
                      >
                        {tag.friendly_name && tag.friendly_name.trim() !== '' 
                          ? `${tag.friendly_name} (${tag.sentinel_id})` 
                          : tag.sentinel_id
                        }
                        {tag.sentinel_type && tag.sentinel_type !== 'Unknown' && ` - ${tag.sentinel_type}`}
                        {tag.is_assigned && watch('sentinel_id') !== tag.sentinel_id && ' - Ya asignado'}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Solo se muestran sentinels que han reportado ubicación en las últimas 24 horas (validación automática).
                </p>
              </div>
            </div>

            {/* Información Adicional */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Información Adicional</h3>
            </div>
            <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capacidad de Almacenamiento
                </label>
                <input
                  type="text"
                  {...register('capacidad_almacenamiento')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: 128GB"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RAM
                </label>
                <input
                  type="text"
                  {...register('ram')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: 8GB"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación Actual
                </label>
                <input
                  type="text"
                  {...register('ubicacion_actual')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Oficina Principal"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Responsable Actual
                </label>
                <input
                  type="text"
                  {...register('responsable_actual')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Juan Pérez"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  {...register('observaciones')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Observaciones adicionales sobre el dispositivo..."
                />
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dispositivos')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarDispositivoSimple;
